import { Controller } from 'stimulus'
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js'
import { getLogger } from '@blueberry/observability'
import { trackStepStarted, trackSignupCompleted } from '../lib/analytics'
import { getQueryParametersString } from '../lib/query_param_utils'
import {
  Flow,
  FormName,
  AccountData,
  AddressData,
  SignupData,
  SignupResponseSuccess,
  TurboSubmitEvent,
  EligibilityData,
  ChildEligibilityData,
  ChildrenEligibilityData,
  ParentEligibilityData,
} from '../lib/types'

const logger = getLogger('signup/checkout_controller')

export default class CheckoutController extends Controller {
  static targets = ['loading', 'notLoading', 'error', 'form']

  declare loadingTargets: HTMLElement[]
  declare notLoadingTargets: HTMLElement[]
  declare errorTargets: HTMLElement[]
  declare formTarget: HTMLFormElement

  static values = {
    flow: String,
    submitUrl: String,
    stripePublishableKey: String,
  }
  declare flowValue: Flow
  declare hasFlowValue: boolean
  declare stripePublishableKeyValue: string
  declare hasStripePublishableKeyValue: boolean
  declare submitUrlValue: string
  declare hasSubmitUrlValue: boolean

  // the aggregated data from all steps
  signupData: SignupData = {}

  private loading = false

  private stripe: Stripe | null = null
  private elements: StripeElements | null = null

  initialize() {
    if (!this.hasFlowValue) {
      logger.error('Flow is required')
      return
    }

    trackStepStarted(this.flowValue, 1, this.signupData)
  }

  connect() {
    if (!this.hasSubmitUrlValue) {
      logger.error('Submit URL is required')
      return
    }

    if (this.flowValue === 'd2c' && !this.hasStripePublishableKeyValue) {
      logger.error('Stripe publishable key is required for D2C flow')
      return
    }
  }

  submitStep(event: SubmitEvent) {
    this.signupData = { ...this.signupData, ...this.getStepData(event) }
  }

  getStepData(event: SubmitEvent): SignupData {
    const inputs = (event.target as HTMLFormElement).querySelectorAll(
      'input, select',
    )

    return this.getFormData(inputs)
  }

  getFormData(inputs: NodeListOf<Element>): SignupData {
    const data = {} as any // eslint-disable-line @typescript-eslint/no-explicit-any

    inputs.forEach((input) => {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement
        )
      ) {
        logger.warn('Invalid input type - got: ', input.tagName)
        return
      }

      const formName = input.dataset.formName as FormName

      // Skip inputs without form names or empty values for optional fields
      if (!formName) {
        logger.warn('Missing form name from input: ', input.name)
        return
      }

      data[formName] = {
        ...(data[formName] ?? {}),
        [input.name]: input.value,
      }
    })

    return data
  }

  // This is mostly called as a Turbo action via templates
  trackStep() {
    const form = this.formTarget
    if (!form) {
      logger.error('Missing form target')
      return
    }

    const step = form.dataset.step
    if (!step) {
      logger.error('Missing step value')
      return
    }

    if (!this.hasFlowValue) {
      logger.error('Missing flow value')
      return
    }

    void trackStepStarted(this.flowValue, Number(step), this.signupData)
  }

  async initializeStripe() {
    if (!this.hasStripePublishableKeyValue) {
      logger.error('Stripe publishable key is missing')
      return
    }

    const stripe = await loadStripe(this.stripePublishableKeyValue)
    this.stripe = stripe

    const elements = stripe?.elements({
      mode: 'setup',
      currency: 'usd',
      paymentMethodCreation: 'manual',
      appearance: {
        variables: {
          colorPrimary: '#235aff',
          colorBackground: '#e0e7ff',
          colorText: '#111827',
          colorDanger: '#ef4444',
          fontFamily: 'Poppins, system-ui, sans-serif',
          borderRadius: '8px',
        },
      },
    })

    if (!elements) {
      logger.error('Stripe Elements not available')
      return
    }
    this.elements = elements

    const paymentElement = elements?.create('payment', {
      layout: 'tabs',
      fields: {
        billingDetails: 'never',
      },
    })

    paymentElement?.mount('#payment-element')
  }

  async createPaymentMethod(account: AccountData, address: AddressData) {
    if (!this.stripe) {
      logger.error('Stripe not initialized')
      return
    }
    if (!this.elements) {
      logger.error('Stripe elements not initialized')
      return
    }

    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.elements,
      params: {
        billing_details: {
          name: `${account.first_name} ${account.last_name}`,
          email: account.email,
          phone: address.phone,
          address: {
            line1: address.address1,
            line2: address.address2 || '',
            city: address.city,
            state: address.state,
            postal_code: address.zip,
            country: 'US',
          },
        },
      },
    })

    if (error) {
      logger.error(error.message)
      return
    }
    return paymentMethod
  }

  async submitD2c(event: TurboSubmitEvent) {
    if (!event.target) {
      logger.error('Missing event target')
      return
    }
    event.preventDefault()

    try {
      if (!this.stripe) {
        logger.error('Stripe not initialized')
        return
      }
      if (!this.elements) {
        logger.error('Stripe elements not initialized')
        return
      }
      if (!this.signupData.account) {
        logger.error('Missing account data')
        return
      }
      if (!this.signupData.address) {
        logger.error('Missing address data')
        return
      }
      if (!this.signupData.plans) {
        logger.error('Missing plans data')
        return
      }

      if (this.loading) return
      this.setLoading()

      // trigger form validation and wallet collections
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        logger.error(submitError.message)
        return
      }

      const paymentMethod = await this.createPaymentMethod(
        this.signupData.account,
        this.signupData.address,
      )

      if (!paymentMethod) {
        logger.error('Missing payment method')
        return
      }

      this.signupData.plans = {
        ...this.signupData.plans,
        payment_method_id: paymentMethod.id,
      }

      const url = `${this.submitUrlValue}?${getQueryParametersString()}`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...this.signupData,
          full_url: window.location.href,
        }),
      })

      const successResponse: SignupResponseSuccess = await response.json()

      if (!successResponse.success) {
        logger.error('Checkout failure')
        return
      }

      void trackSignupCompleted(
        this.flowValue,
        this.signupData,
        successResponse,
      )
      window.location.href = encodeURI(successResponse.redirect_to)
      return
    } catch (error) {
      logger.error('Error submitting D2C signup', error)
      this.showErrors()
    } finally {
      this.setNotLoading()
    }
  }

  async submitInsurance(event: TurboSubmitEvent) {
    if (!event.detail.success) return

    event.preventDefault()

    if (this.loading) return
    this.setLoading()

    try {
      if (!this.signupData.eligibility) {
        logger.error('Missing eligibility data')
        return
      }

      if (!this.validateEligibilityData(this.signupData.eligibility)) {
        logger.error('Invalid eligibility data')
        return
      }

      const children = this.formatChildrenEligibilities(
        this.signupData.eligibility,
      )

      const url = `${this.submitUrlValue}?${getQueryParametersString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...this.signupData,
          child_eligibilities: children,
          parent_eligibility: this.signupData.eligibility,
          full_url: window.location.href,
        }),
      })

      const successResponse: SignupResponseSuccess = await response.json()

      void trackSignupCompleted(
        this.flowValue,
        this.signupData,
        successResponse,
      )
      window.location.pathname = successResponse.redirect_to
    } catch (error) {
      logger.error('Error submitting insurance signup', error)
      this.showErrors()
    } finally {
      this.setNotLoading()
    }
  }

  validateEligibilityData(eligibility: EligibilityData) {
    switch (eligibility.eligibility_type) {
      case 'children':
        return this.validateChildrenEligibilityData(eligibility)
      case 'child':
        return this.validateChildEligibilityData(eligibility)
      case 'parent':
        return this.validateParentEligibilityData(eligibility)
      default:
        logger.error('Invalid eligibility type')
        return false
    }
  }

  validateChildrenEligibilityData(eligibility: ChildrenEligibilityData) {
    return this.flowValue === 'insurance_child' && !!eligibility.children.length
  }

  validateChildEligibilityData(eligibility: ChildEligibilityData) {
    const complete_child_fields =
      eligibility.child_date_of_birth &&
      eligibility.child_first_name &&
      eligibility.child_last_name

    const required_fields =
      complete_child_fields ||
      eligibility.insurance_member_id ||
      eligibility.child_medicaid_id

    return this.flowValue === 'insurance_child' && required_fields
  }

  validateParentEligibilityData(eligibility: ParentEligibilityData) {
    return (
      this.flowValue === 'insurance_parent' &&
      !!eligibility.insurance_parent_member_id
    )
  }

  formatChildrenEligibilities(eligibility: EligibilityData) {
    if (eligibility.eligibility_type === 'parent') {
      return []
    }

    if (eligibility.eligibility_type === 'children') {
      // Multi-child flow sends children as a JSON string from the backend
      if (typeof eligibility.children === 'string') {
        try {
          return JSON.parse(eligibility.children)
        } catch (e) {
          logger.error('Failed to parse children JSON string', e)
          return []
        }
      }
      return eligibility.children
    }

    return [
      {
        child_first_name: eligibility.child_first_name,
        child_last_name: eligibility.child_last_name,
        child_date_of_birth: eligibility.child_date_of_birth,
        insurance_member_id: eligibility.insurance_member_id,
        child_medicaid_id: eligibility.child_medicaid_id,
      },
    ]
  }

  setLoading() {
    this.loading = true
    this.loadingTargets.forEach((target) => (target.hidden = false))
    this.notLoadingTargets.forEach((target) => (target.hidden = true))
  }

  setNotLoading() {
    this.loading = false
    this.loadingTargets.forEach((target) => (target.hidden = true))
    this.notLoadingTargets.forEach((target) => (target.hidden = false))
  }

  showErrors() {
    this.errorTargets.forEach((target) => (target.hidden = false))
  }
}
